import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
  VpnKey as VpnKeyIcon,
  Layers as LayersIcon,
  PowerSettingsNew as ToggleIcon,
} from '@mui/icons-material';

import {
  GisCredential,
  GisCredentialSecrets,
  PublishedLayer,
  isLayerBusy,
  useCreateGisCredential,
  useDeleteGisCredential,
  useGisCredentials,
  useGisServicesSetting,
  usePublishedLayers,
  useRemovePublishedLayer,
  useResetGisCredentialKey,
  useResetGisCredentialPassword,
  useSetGisCredentialActive,
  useUpdateGisServicesSetting,
} from '../../api/gis';
import AdminPageHeader from './AdminPageHeader';

/**
 * Admin → GIS services: how the user connects their own GIS to Mitigate —
 * connection URLs, the credentials they create, and the fires they've published.
 *
 * Copy is written to the user, in their terms. Never describe them as
 * "customers"/"organisations"/"tenants" or mention GeoServer/workspaces.
 */

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      sx={{ mb: 1.5 }}
      InputProps={{
        readOnly: true,
        sx: { fontFamily: 'monospace', fontSize: 13 },
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton size="small" onClick={copy}>
                {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
}

function SecretRevealDialog({
  open,
  onClose,
  credential,
  title,
  wmsUrl,
}: {
  open: boolean;
  onClose: () => void;
  credential: GisCredentialSecrets | null;
  title: string;
  wmsUrl: string;
}) {
  const password = credential?.password;
  const authkey = credential?.authkey;
  const keyedUrl = authkey && wmsUrl ? `${wmsUrl}?authkey=${authkey}` : '';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <KeyIcon color="warning" />
        {title}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Copy these now — they <strong>will not be shown again</strong>.
        </Alert>
        {credential && (
          <>
            <CopyField label="Username" value={credential.username} />
            {password ? <CopyField label="Password" value={password} /> : null}
            {authkey ? <CopyField label="Key" value={authkey} /> : null}
            {keyedUrl ? <CopyField label="WMS URL with the key" value={keyedUrl} /> : null}
          </>
        )}
        <Typography variant="caption" color="text.secondary" display="block">
          QGIS and ArcGIS Pro can use the username and password. ArcGIS Online and Portal
          cannot store those for an OGC service, so use the key there instead: add it as the
          custom parameter <b>authkey</b>, or paste the URL above.
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Anyone holding the key can read your layers, so treat it like a password. Rotate it
          here to cut off access.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          I've saved these
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  color = 'error',
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel: string;
  color?: 'error' | 'warning' | 'success';
  loading: boolean;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color={color}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const STATUS_COLOR: Record<PublishedLayer['status'], 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  PENDING: 'info',
  PUBLISHING: 'info',
  PUBLISHED: 'success',
  FAILED: 'error',
  UNPUBLISHING: 'warning',
};

type ConfirmType = 'reset' | 'resetkey' | 'toggle' | 'delete';

/** Title, message, button label and colour for each credential action. */
function confirmCopy(type: ConfirmType, target: GisCredential) {
  switch (type) {
    case 'reset':
      return {
        title: 'Rotate password?',
        message: `A new password will be generated for "${target.name}". The current one stops working immediately.`,
        confirmLabel: 'Rotate password',
        color: 'warning' as const,
      };
    case 'resetkey':
      return {
        title: 'Rotate key?',
        message: `A new key will be generated for "${target.name}". Anything using the current key stops working within a minute.`,
        confirmLabel: 'Rotate key',
        color: 'warning' as const,
      };
    case 'toggle':
      return target.is_active
        ? {
            title: 'Disable credential?',
            message: `"${target.name}" will no longer be able to connect, and its key is withdrawn.`,
            confirmLabel: 'Disable',
            color: 'warning' as const,
          }
        : {
            title: 'Enable credential?',
            message: `"${target.name}" will be able to connect again with its existing password, and a new key will be issued.`,
            confirmLabel: 'Enable',
            color: 'success' as const,
          };
    case 'delete':
    default:
      return {
        title: 'Delete credential?',
        message: `"${target.name}" will be removed. This cannot be undone.`,
        confirmLabel: 'Delete',
        color: 'error' as const,
      };
  }
}

export default function GisServicesManagement() {
  const { data: setting, isLoading: loadingSetting } = useGisServicesSetting();
  const { data: credentials = [], isLoading: loadingCreds } = useGisCredentials();
  const { data: published = [] } = usePublishedLayers();
  const createCredential = useCreateGisCredential();
  const resetPassword = useResetGisCredentialPassword();
  const resetKey = useResetGisCredentialKey();
  const setActive = useSetGisCredentialActive();
  const deleteCredential = useDeleteGisCredential();
  const updateSetting = useUpdateGisServicesSetting();
  const removeLayer = useRemovePublishedLayer();

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState('');
  const [reveal, setReveal] = useState<{ open: boolean; credential: GisCredentialSecrets | null; title: string }>({
    open: false,
    credential: null,
    title: '',
  });
  const [confirm, setConfirm] = useState<{ type: ConfirmType; target: GisCredential } | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const configured = !!setting?.environment_configured;
  const authkeyReady = !!setting?.authkey_available;
  const ready = configured;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) {
      setCreateError('Name is required.');
      return;
    }
    try {
      const cred = await createCredential.mutateAsync({ name: newName.trim() });
      setCreateOpen(false);
      setNewName('');
      setCreateError('');
      setReveal({ open: true, credential: cred, title: 'Credential created' });
    } catch (err) {
      setCreateError((err as { message?: string })?.message || 'Could not create the credential.');
    }
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    const { type, target } = confirm;
    try {
      if (type === 'reset') {
        const cred = await resetPassword.mutateAsync(target.id);
        setConfirm(null);
        setReveal({ open: true, credential: cred, title: 'Password rotated' });
      } else if (type === 'resetkey') {
        const cred = await resetKey.mutateAsync(target.id);
        setConfirm(null);
        setReveal({ open: true, credential: cred, title: 'Key rotated' });
      } else if (type === 'toggle') {
        const cred = await setActive.mutateAsync({ id: target.id, active: !target.is_active });
        setConfirm(null);
        if (target.is_active) {
          setToast({ message: 'Credential disabled. Its key no longer works.', severity: 'success' });
        } else {
          // Disabling withdrew the key, so enabling issues a fresh one.
          setReveal({ open: true, credential: cred, title: 'Credential enabled' });
        }
      } else {
        await deleteCredential.mutateAsync(target.id);
        setConfirm(null);
        setToast({ message: 'Credential deleted.', severity: 'success' });
      }
    } catch (err) {
      setToast({ message: (err as { message?: string })?.message || 'Something went wrong.', severity: 'error' });
    }
  };

  const busy =
    resetPassword.isPending || resetKey.isPending || setActive.isPending || deleteCredential.isPending;

  if (loadingSetting || loadingCreds) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <AdminPageHeader
        title="GIS services"
        description="Show published fires in ArcGIS, QGIS or any WMS, WMTS or WCS client."
        audience="admins"
      />

      {!configured && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          GIS services aren't available in this environment yet.
        </Alert>
      )}

      {/* 1. Connect your GIS */}
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
        Connect your GIS
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Click a fire on the map and choose Add to GIS, then add one of these URLs to your GIS
        with a credential from below. Your GIS sees four layers, one per metric. They show
        the latest fire by default, and a time slider or time range steps back through every
        fire you've added — each layer's time dimension is the fire's start time. WMS shows
        the styled layers, WMTS serves cached tiles, and WCS gives you the values themselves
        (arrival time in minutes, rate of spread in m/min, intensity in kW/m, flame height in m).
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {setting && setting.wms_url ? (
          <>
            <CopyField label="WMS" value={setting.wms_url} />
            <CopyField label="WMTS" value={setting.wmts_url} />
            <CopyField label="WCS" value={setting.wcs_url} />
            <Typography variant="caption" color="text.secondary">
              Layers: {setting.layers.join(', ')}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Connection details will appear here once GIS services are available.
          </Typography>
        )}
      </Paper>

      {/* 2. GIS credentials */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="subtitle1">GIS credentials</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!ready}
          onClick={() => setCreateOpen(true)}
        >
          Create credential
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        A username and password for your GIS to sign in with, plus a key for ArcGIS Online
        and Portal. Make one per GIS so you can rotate or disable each on its own.
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Password</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Key</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {credentials.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No credentials yet. Create one to connect your GIS.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              credentials.map((cred) => (
                <TableRow key={cred.id} sx={{ opacity: cred.is_active ? 1 : 0.6 }}>
                  <TableCell>{cred.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {cred.username}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                      ••••••••{cred.password_hint}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                      {cred.authkey_hint ? `••••${cred.authkey_hint}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={cred.is_active ? 'Enabled' : 'Disabled'}
                      color={cred.is_active ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{new Date(cred.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Tooltip title="Rotate password">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!ready || !cred.is_active}
                          onClick={() => setConfirm({ type: 'reset', target: cred })}
                        >
                          <KeyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={authkeyReady ? 'Rotate key' : 'Keys are not available in this environment yet'}
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!ready || !authkeyReady || !cred.is_active}
                          onClick={() => setConfirm({ type: 'resetkey', target: cred })}
                        >
                          <VpnKeyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={cred.is_active ? 'Disable' : 'Enable'}>
                      <span>
                        <IconButton
                          size="small"
                          color={cred.is_active ? 'warning' : 'success'}
                          disabled={!configured}
                          onClick={() => setConfirm({ type: 'toggle', target: cred })}
                        >
                          <ToggleIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={!configured}
                          onClick={() => setConfirm({ type: 'delete', target: cred })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 3. Published fires */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={0.5}>
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <LayersIcon fontSize="small" /> Published fires
        </Typography>
        <FormControl size="small" sx={{ minWidth: 220 }} disabled={!setting || updateSetting.isPending}>
          <InputLabel id="gis-retention-label">Keep fires for</InputLabel>
          <Select
            labelId="gis-retention-label"
            label="Keep fires for"
            value={setting?.gis_retention_days ?? 'keep'}
            onChange={(e) => {
              const v = e.target.value;
              updateSetting.mutate(
                { gis_retention_days: v === 'keep' ? null : Number(v) },
                {
                  onSuccess: () => setToast({ message: 'Saved.', severity: 'success' }),
                  onError: (err: { message?: string }) =>
                    setToast({ message: err?.message || 'Could not save.', severity: 'error' }),
                }
              );
            }}
          >
            {(setting?.retention_choices ?? [null, 30, 90, 180, 365]).map((c) => (
              <MenuItem key={c ?? 'keep'} value={c ?? 'keep'}>
                {c === null ? 'Until I remove them' : `${c} days`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Each fire you add appears in all four layers at its start time, under the id shown
        here. Fires stay in your GIS until you remove them here or from the map, or, if you
        set a period above, until that long after the fire's start time.
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Fire</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Simulation</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Fire time</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Added</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {published.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Nothing published yet. Click a fire in a simulation and choose Add to GIS.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              published.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {row.layer_group}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.project_name}</TableCell>
                  <TableCell>{row.simulation_name}</TableCell>
                  <TableCell>{row.fire_time ? new Date(row.fire_time).toLocaleString() : '—'}</TableCell>
                  <TableCell>
                    <Tooltip title={row.publish_error || row.publish_stage || ''}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={STATUS_COLOR[row.status]}
                        icon={isLayerBusy(row) ? <CircularProgress size={12} /> : undefined}
                        label={row.status.charAt(0) + row.status.slice(1).toLowerCase()}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {row.published_at ? new Date(row.published_at).toLocaleString() : '—'}
                    {row.published_by_username ? ` · ${row.published_by_username}` : ''}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={isLayerBusy(row) ? 'Busy' : 'Remove from your GIS'}>
                      <span>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon fontSize="small" />}
                          disabled={isLayerBusy(row) || !configured || removeLayer.isPending}
                          onClick={() =>
                            removeLayer.mutate(row.id, {
                              onSuccess: () => setToast({ message: 'Removing from your GIS.', severity: 'success' }),
                              onError: (err: { message?: string }) =>
                                setToast({ message: err?.message || 'Could not remove.', severity: 'error' }),
                            })
                          }
                        >
                          Remove
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create credential</DialogTitle>
        <form onSubmit={handleCreate}>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              A username and password will be generated for your GIS to connect with. The
              password is shown once — copy it straight away.
            </DialogContentText>
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createError}
              </Alert>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Name"
              fullWidth
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Our ArcGIS Online"
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)} disabled={createCredential.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createCredential.isPending}
              startIcon={createCredential.isPending ? <CircularProgress size={16} /> : <AddIcon />}
            >
              Create credential
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <SecretRevealDialog
        open={reveal.open}
        onClose={() => setReveal((r) => ({ ...r, open: false }))}
        credential={reveal.credential}
        title={reveal.title}
        wmsUrl={setting?.wms_url ?? ''}
      />

      {confirm && (
        <ConfirmDialog
          open
          onClose={() => setConfirm(null)}
          onConfirm={handleConfirm}
          loading={busy}
          {...confirmCopy(confirm.type, confirm.target)}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'success'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
